import { useMemo } from 'react';
import { Box, Button, Checkbox, MenuItem, Typography } from '@mui/material';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { FormCard } from 'components/Card';
import { AppSelectField, AppTextField } from 'components/form-fields';
import { getTrans } from 'modules/i18n';
import { ExperienceEntry } from 'utils/cv-form-db/types';
import { ButtonContainer } from '../../components/styledComponents';
import { initialValues } from './constants';
import { useExperienceForm } from './hooks';
import { generateDateOptions, validateExperience } from './utils';

interface ExperienceFormProps {
  mode?: 'add' | 'edit';
  initialValues?: ExperienceEntry;
  onSubmit: (experience: ExperienceEntry) => void;
  onCancel: () => void;
}

const SubmitExperienceButton = ({
  isEditMode,
  isComplete
}: {
  isEditMode: boolean;
  isComplete: boolean;
}) => (
  <ButtonContainer sx={{ pb: 1 }}>
    <Button
      fullWidth
      type="submit"
      data-testid={
        isEditMode ? 'save-experience-button' : 'add-experience-button'
      }
      color="secondary"
      variant="contained"
      sx={{ minWidth: 252 }}
      disabled={!isComplete}
    >
      {isEditMode
        ? getTrans('general.update')
        : getTrans(
            'cvBuilderPage.professionalExperience.button.addExperience.label'
          )}
    </Button>
  </ButtonContainer>
);

const CancelExperienceButton = ({ onCancel }: { onCancel: () => void }) => (
  <ButtonContainer sx={{ mt: 2 }}>
    <Button
      fullWidth
      data-testid="cancel-experience-button"
      color="secondary"
      variant="outlined"
      sx={{ minWidth: 252 }}
      onClick={onCancel}
    >
      {getTrans('general.cancel')}
    </Button>
  </ButtonContainer>
);

const InnerExperienceForm = ({
  mode = 'add',
  onCancel
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
}) => {
  const { values } = useFormikContext<ExperienceEntry>();

  const isEditMode = mode === 'edit';

  const { isComplete } = useExperienceForm(values);

  const { startDateOptions, endDateOptions } = useMemo(
    () => generateDateOptions(values.startDate),
    [values.startDate]
  );

  const currentlyWorking = values.currentlyWorking;

  return (
    <Form
      data-testid={`${isEditMode ? 'edit' : 'add'}-experience-form`}
      style={{
        flexBasis: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <FormCard sx={{ marginBottom: 2 }}>
        <AppTextField
          sx={{ mb: 2, mt: 1 }}
          data-dd-privacy="mask"
          name="companyName"
          label={getTrans(
            'cvBuilderPage.professionalExperience.form.company.label'
          )}
          variant="outlined"
          fullWidth
        />
        <AppSelectField
          fullWidth
          displayValidationError
          data-dd-privacy="mask"
          variant="outlined"
          name="startDate"
          label={getTrans(
            'cvBuilderPage.professionalExperience.form.startDate.label'
          )}
        >
          {startDateOptions.map(option => (
            <MenuItem
              key={option.label}
              data-testid={`startDate-select-field-option-${option.value}`}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </AppSelectField>
        {!currentlyWorking && (
          <>
            <Box sx={{ mb: 3 }} />
            <AppSelectField
              fullWidth
              displayValidationError
              data-dd-privacy="mask"
              variant="outlined"
              name="endDate"
              label={getTrans(
                'cvBuilderPage.professionalExperience.form.endDate.label'
              )}
            >
              {endDateOptions.map(option => (
                <MenuItem
                  key={option.label}
                  data-testid={`endDate-select-field-option-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </AppSelectField>
          </>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
          <Field as={Checkbox} type="checkbox" name="currentlyWorking" />
          <Typography>
            {getTrans(
              'cvBuilderPage.professionalExperience.form.currentlyWorking.label'
            )}
          </Typography>
        </Box>

        <AppTextField
          sx={{ mb: 2 }}
          data-dd-privacy="mask"
          name="position"
          label={getTrans(
            'cvBuilderPage.professionalExperience.form.position.label'
          )}
          variant="outlined"
          fullWidth
        />
        {/* The one field that decides whether an operator can judge this talent — and the
            company after them. The 1000-char counter already mirrors pro-core's
            @Size(max = 1000); the placeholder is what actually raises the quality, by
            asking for specifics instead of leaving the box blank and unexplained.
            minRows makes it look like the several sentences it wants. */}
        <AppTextField
          maxCharacterCount={1000}
          data-dd-privacy="mask"
          name="description"
          label={getTrans(
            'cvBuilderPage.professionalExperience.form.jobDescription.label'
          )}
          placeholder={getTrans(
            'cvBuilderPage.professionalExperience.form.jobDescription.placeholder'
          )}
          variant="outlined"
          fullWidth
          multiline
          minRows={4}
        />
      </FormCard>
      <Box sx={{ flex: 1 }} />
      <SubmitExperienceButton isEditMode={isEditMode} isComplete={isComplete} />
      <CancelExperienceButton onCancel={onCancel} />
    </Form>
  );
};

export const ExperienceForm = ({
  mode = 'add',
  initialValues: editedValues,
  onSubmit,
  onCancel
}: ExperienceFormProps) => (
  <Formik
    initialValues={editedValues || initialValues}
    onSubmit={onSubmit}
    validate={validateExperience}
    enableReinitialize
  >
    <InnerExperienceForm mode={mode} onCancel={onCancel} />
  </Formik>
);
